import {match} from 'ts-pattern';

import {isString} from 'ramda-adjunct';

import {workshopRoutes} from '@dms/routes';
import {
  catchUnhandledDataGridActions,
  DmsDataGrid,
  logError,
  queryParams,
  workshopSearchParams,
} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import {SOURCE_DATA_GRID, type ActionCallback, type QueryFilterObject} from 'features/eag-grid';

import {SERVICE_CASE_PDF_COLUMNS} from '../../../../constants/SERVICE_CASE_PDF_COLUMNS';
import {useServiceCasePdfExport} from '../../../../hooks/useServiceCasePdfExport';

const PREDEFINED_COLUMNS = ['state'] as const;
const SERVICE_CASE_STATE_DRAFT = 'DRAFT';
const REDIRECT_SOURCE_SERVICE_CASES = 'service-cases';
const SERVICE_CASE_GRID_CODE = 'service-case';

const queryModifier = (filter: QueryFilterObject): QueryFilterObject => ({
  ...filter,
  vehicleId: undefined,
});

export function ServiceCaseOverview() {
  const navigate = useNavigate();
  const onPdfExportCallback = useServiceCasePdfExport();

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    getColumnData,
    sourceSearchParams,
  }) => {
    if (!isString(rowId)) {
      return logError(`Bulk invocation of ${actionKey} is not implemented`);
    }
    const URLSourceSearchParams = new URLSearchParams(sourceSearchParams);

    const serviceCaseState = getColumnData<{key: string}>('state')?.value?.key;
    const serviceCaseId = rowId;
    const path =
      serviceCaseState === SERVICE_CASE_STATE_DRAFT
        ? composePath(workshopRoutes.draftServiceCase, {
            params: {id: serviceCaseId},
            queryParams: {
              tab: workshopSearchParams.create.serviceJobs,
              [SOURCE_DATA_GRID]: URLSourceSearchParams.get(SOURCE_DATA_GRID),
              [queryParams.REDIRECT_SOURCE]: REDIRECT_SOURCE_SERVICE_CASES,
            },
          })
        : composePath(workshopRoutes.serviceCaseDetail, {
            params: {id: serviceCaseId},
            queryParams: {
              [queryParams.REDIRECT_SOURCE]: REDIRECT_SOURCE_SERVICE_CASES,
              [SOURCE_DATA_GRID]: URLSourceSearchParams.get(SOURCE_DATA_GRID),
            },
          });

    match(actionKey)
      .with('redirectDetail', 'detail', () => navigate(path))
      .with('redirectDetail_newTab', 'detail_newTab', () =>
        window.open(path, '_blank', 'noopener,noreferrer')
      )
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <DmsDataGrid
      data-testid="service-case-overview"
      gridCode={SERVICE_CASE_GRID_CODE}
      actionCallback={actionCallback}
      predefinedColumns={PREDEFINED_COLUMNS}
      pdfColumns={SERVICE_CASE_PDF_COLUMNS}
      onPdfExportCallback={onPdfExportCallback}
      queryModifier={queryModifier}
    />
  );
}
